/**
 * ShelfRecDialog.tsx — 书架推荐书籍 新建 / 编辑弹窗（ADM-OPS-S009）
 *
 * 移植自原型 design/Novel-admin/src/dialogs3.jsx 116–198（ShelfRecDialog）。
 * 机械转换：JS→TS；window.__multiLang → 受控状态；window.NA_DATA.BOOKS → useBooks()；
 *   原型 DialogShell → ~/ui/Dialog；视觉层（JSX/className/inline-style）与原型逐字一致。
 *
 * 接真（PR7）：「新增 / 保存」→ createShelfRecommendation（POST）/ updateShelfRecommendation（PATCH /:id），operator+ mfa。
 *   - 两层：全部 → app_id null 写全局层；具体 App → 写该 App 层。novel_id = 当前语言 per-locale 行原值。
 *   - 编辑省略 novel_id 不可改（service 拒换书组）→ 编辑态锁定书籍、仅可改 sort_order / is_listed；
 *   - 推荐语选填：加入、上架均不校验推荐语（A2 §8.5）。
 *   - 新建 POST 传 sort_order（默认末尾）；编辑 PATCH 只传改动字段，不回传整行。
 *   - 成功 → toast + setQueryData 合并 + invalidate（mutation 内）；失败 → 错误 toast 不假成功、表单不丢。
 *   - save 受 canUpsert gate（viewer / 会话内降级 → save 禁用，防发被拒 PUT）。
 *
 * NOT-in-scope（计划 §五）：**推荐语录入（recommendation_text_i18n）不在本端点**——
 *   Create/UpdateShelfRecommendationRequest 均无推荐语字段（前者 { app_id, novel_id, sort_order }，后者 { sort_order?, is_listed? }）；
 *   推荐语复用 novels.recommendation_text_i18n、由书籍编辑（ADM-BOK-S003）维护。本弹窗只读展示选中书的现有推荐语池
 *   （从 JOIN 响应来），并提供只读快捷入口前往所选书籍详情维护推荐语；快捷入口不提交写请求。
 */

import type { ShelfRecommendationResponse } from '@novel-admin/shared';
import { useState } from 'react';
import { useBooks } from '~/data/queries';
import type { ShelfRecommendationMutations } from '~/data/use-ops-shelf-popup';
import { useBookLanguageRow } from '~/hooks/use-book-language-row';
import { Icons } from '~/lib/icons';
import { BookIdentityCard } from '~/ui/BookIdentityCard';
import { BookLanguageRowPicker } from '~/ui/BookLanguageRowPicker';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UIFormRow } from '~/ui/FormRow';
import { Switch } from '~/ui/Switch';
import { errorToast, successToast } from '../use-ops-mutations';

const FormRow = (p: FormRowProps) => (
  <UIFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';

export function ShelfRecDialog({
  rec,
  app,
  lang,
  /** 该层下既有推荐总数 — 新建默认排到末尾（sort_order = 既有计数） */
  existingCount,
  mutations,
  canUpsert,
  onClose,
  onOpenBookDetails,
}: {
  rec?: ShelfRecommendationResponse;
  app: { id: string; name: string };
  lang: string;
  existingCount: number;
  mutations: ShelfRecommendationMutations;
  canUpsert: boolean;
  onClose: () => void;
  onOpenBookDetails: (novelId: string) => void;
}) {
  const { data: books } = useBooks();
  const isEdit = !!rec;
  const writeAppId = app.id === 'all' ? null : app.id;

  // 编辑态锁定书籍（novel_id 不可改）；新建态用搜索选书
  const [novelId, setNovelId] = useState(rec?.novel_id ?? '');
  // 已选书行详情：新建态给 picker 回显，编辑态给只读身份卡片 —— 两态都要，故不按 isEdit 收窄
  const selectedBookQ = useBookLanguageRow(novelId);
  const [listed, setListed] = useState(rec?.is_listed ?? false);

  const selBook =
    books.find((b) => b.languageCodes.some((l) => l.novelId === novelId)) ??
    books.find((b) => b.id === novelId);
  // 选中书的现有推荐语池（编辑态从 JOIN 响应；新建态本地无 JOIN → 仅在编辑态展示既有推荐语）
  const recI18n = rec?.recommendation_text_i18n ?? null;
  const recommendationEntries = Object.entries(recI18n ?? {})
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    .map(([locale, text]) => [locale, text.trim()] as const)
    .filter(([, text]) => text.length > 0);
  const recLangs = recommendationEntries.length;

  const busy = mutations.create.isPending || mutations.update.isPending;
  const canSave = canUpsert && !!novelId && !busy;
  const canOpenBookDetails = !!novelId && !busy;
  const openBookDetailsTitle = !novelId
    ? '请先选择书籍'
    : busy
      ? '正在保存书架推荐，请稍候'
      : '前往书籍详情维护推荐语';

  const submit = () => {
    if (!canSave) return;
    const handlers = {
      onSuccess: () => {
        successToast(
          `${isEdit ? '已保存' : '已新增'}书架推荐 · ${selBook?.displayTitle || novelId}${listed ? ' · 已上架' : ' · 草稿'}`,
        );
        onClose();
      },
      onError: (err: unknown) => errorToast(err, isEdit ? '保存书架推荐失败' : '新增书架推荐失败'),
    };

    if (isEdit) {
      // PATCH（§8.3）：只带本弹窗能改的 is_listed —— app_id / novel_id 契约明列不可改
      // （前者定所属层、后者定配置对象与逻辑语言段），sort_order 本弹窗不编辑故不传。
      mutations.update.mutate({ id: rec.id, body: { is_listed: listed } }, handlers);
      return;
    }
    // POST（§8.2）：本组唯一允许在新建时带 is_listed 的端点 —— 契约明写「POST body 显式带
    // is_listed=true 则直接上架」，故运营勾了上架就直接建成上架态。
    mutations.create.mutate(
      {
        app_id: writeAppId,
        language: lang,
        novel_id: novelId,
        sort_order: existingCount, // 新建排到末尾
        is_listed: listed,
      },
      handlers,
    );
  };

  const submitTitle = !canUpsert ? FORBIDDEN_ROLE_TITLE : !novelId ? '请选择书籍' : undefined;

  return (
    <Dialog
      width={680}
      title={
        isEdit
          ? `编辑书架推荐 · ${selBook?.displayTitle || rec.novel_title || '书籍'}`
          : '新增推荐书籍'
      }
      subtitle={`ADM-OPS-S009 · ${writeAppId ? app.name : '该语言全局'} · 候选仅 ${lang.toUpperCase()} 书行 · 推荐语由书籍编辑维护`}
      icon="IconBook"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            推荐语选填，在书籍编辑维护
          </div>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!canSave}
            title={submitTitle}
            onClick={submit}
          >
            {isEdit ? '保存' : '新增'}
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow
          label="书籍"
          required
          hint={isEdit ? '书组不可改 · 换书请删除后重建' : '按书编码 / 书名 / 作者搜索'}
        >
          {/* 编辑态锁定书组（service 拒换书组），保持既有只读展示；
              新建态整体交给统一的 BookLanguageRowPicker —— 它自带已选卡片与「更换」，
              且候选由服务端按 lang + status=LISTED 收窄，不再受本地 useBooks 已加载数据与 slice(0,5) 限制。 */}
          {isEdit ? (
            selectedBookQ.data ? (
              <BookIdentityCard book={selectedBookQ.data} />
            ) : selBook ? (
              // 详情还没到（或查失败）时退回列表行已有的字段，不空着
              <div className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                {selBook.displayTitle}
              </div>
            ) : (
              // 编辑态但 books 未命中 → 只显示书名，不回退 uuid
              <div className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                {rec.novel_title || '书编码未回传'}
              </div>
            )
          ) : (
            <BookLanguageRowPicker
              value={novelId}
              book={selectedBookQ.data}
              onChange={setNovelId}
              // 本层是「该语言全局」：novel_id 必须是当前语言的 per-locale 行（见文件头接真说明）
              lockedLocale={lang}
              bookLookupFailed={
                selectedBookQ.isError || (selectedBookQ.isSuccess && !selectedBookQ.data)
              }
            />
          )}
        </FormRow>

        {/* 推荐语展示（只读）：录入走书籍编辑（ADM-BOK-S003），本端点无推荐语字段（计划 §五）*/}
        <FormRow label="推荐语" hint="选填 · 录入在书籍编辑（ADM-BOK-S003）· 本处只读展示">
          <div style={{ maxWidth: 420 }}>
            {recommendationEntries.length > 0 ? (
              <div
                className="card-flat"
                style={{ padding: '8px 12px', background: 'var(--bg-subtle)' }}
              >
                {recommendationEntries.map(([locale, text]) => (
                  <div key={locale} style={{ fontSize: 11.5, lineHeight: 1.6 }}>
                    <span className="mono" style={{ color: 'var(--fg-faint)', marginRight: 6 }}>
                      {locale.toUpperCase()}
                    </span>
                    {text}
                  </div>
                ))}
                <div
                  className="mono"
                  style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 4 }}
                >
                  已维护 {recLangs} 种语言
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                {isEdit ? '该书暂无推荐语' : '选择书籍后可在书籍编辑维护推荐语'}
              </div>
            )}
            <button
              type="button"
              className="btn btn-sm"
              style={{ marginTop: 8 }}
              disabled={!canOpenBookDetails}
              title={openBookDetailsTitle}
              onClick={() => onOpenBookDetails(novelId)}
            >
              <Icons.IconExternal size={11} /> 前往书籍详情维护推荐语
            </button>
          </div>
        </FormRow>

        <FormRow label="上架状态">
          <Switch
            on={listed}
            size="sm"
            onChange={(next) => setListed(next)}
            disabled={!canUpsert}
          />
        </FormRow>
      </div>
    </Dialog>
  );
}
